import type { DownloadProps } from "../types";
import DynamicMultiStepForm from "../dynamic-multi-step-form";
import {
  API_ENDPOINTS,
  oauth_domain,
  oauth_key,
} from "../../../../constants";
import { getSessionId, getCookie } from "../../../../libs/fns";
import { useBottomSheet } from "../../../../contexts/bottom-sheet";
import {
  buildCustomerName,
  buildCustomerContact,
  buildSubmissionPayload,
} from "../../../../libs/formHelpers";

type Props = {
  formProps: DownloadProps;
};

export default function DownloadForm({ formProps }: Props) {
  const { openBottomSheet } = useBottomSheet();

  // PDF判定: ブラウザで開かれるタイプかチェック
  function isPdfFile(filename: string): boolean {
    return filename.toLowerCase().endsWith('.pdf');
  }

  // ファイルダウンロード用のヘルパー関数（PDF専用: Blob使用）
  async function downloadFileWithBlob(url: string, filename: string) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Fetch failed');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Blobメモリ解放
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Blob download failed:', error);
      // フォールバック: 直接リンク
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // ファイルダウンロード用のヘルパー関数（PDF以外: 通常）
  function downloadFileWithAnchor(url: string, filename: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function callback(values: Record<string, unknown>) {
    const session_id = getSessionId();
    if (!session_id || !oauth_key || !oauth_domain) {
      console.error("[DownloadForm] セッション情報がありません。");
      alert("セッション情報が見つかりません。ページを再読み込みしてください。");
      return;
    }

    try {
      // CSRF token取得
      const csrftoken = getCookie("csrftoken");

      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      if (csrftoken) {
        headers["X-CSRFToken"] = csrftoken;
      }

      // フォームデータから不要なフィールドを除外
      const { uid: _uid, ...cleanValues } = values as any;
      const stringValues = Object.fromEntries(
        Object.entries(cleanValues).map(([k, v]) => [k, String(v ?? "")])
      ) as Record<string, string>;

      // フォーム構造を使って顧客情報・ペイロードを動的に生成
      const form_data = buildSubmissionPayload(stringValues, formProps);
      const customer_name = buildCustomerName(stringValues, formProps);
      const { mail: customer_mail, phone: customer_phone } = buildCustomerContact(stringValues, formProps);

      // フォームデータをAPIに送信
      let requestBody: any = {
        oauth_key,
        oauth_domain,
        session_id,
        form_data,
        customer_name,
        customer_mail,
        customer_phone,
      };

      // 念のためuidを削除
      delete requestBody.uid;

      // API送信データログ
      console.log("[DownloadForm API] 送信データ:", {
        endpoint: API_ENDPOINTS.get_form_data,
        form_data,
        customer_name,
        customer_mail,
        customer_phone,
      });

      const response = await fetch(API_ENDPOINTS.get_form_data, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
        // oauth_key/oauth_domainで認証する公開ウィジェットAPIのためCookie不要
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[DownloadForm] API Error Response:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      // APIレスポンスログ
      console.log("[DownloadForm API] レスポンス受信成功:", responseData);

      // 送信完了ページに遷移（Contextを使用）
      openBottomSheet("THANKS");

      // ダウンロード開始（複数ファイル対応）
      setTimeout(async () => {
        // download_filesがある場合は複数ファイルダウンロード
        if (formProps.download_files && formProps.download_files.length > 0) {
          for (let i = 0; i < formProps.download_files.length; i++) {
            const fileUrl = formProps.download_files[i];
            const filename = fileUrl.split('/').pop() || 'download';

            if (i > 0) {
              await new Promise(resolve => setTimeout(resolve, 300));
            }

            // PDFの場合はBlob、それ以外は通常ダウンロード
            if (isPdfFile(filename)) {
              await downloadFileWithBlob(fileUrl, filename);
            } else {
              downloadFileWithAnchor(fileUrl, filename);
            }
          }
        } else if (formProps.url) {
          // 単一URLの場合
          const filename = formProps.url.split('/').pop() || 'download';
          if (isPdfFile(filename)) {
            await downloadFileWithBlob(formProps.url, filename);
          } else {
            downloadFileWithAnchor(formProps.url, filename);
          }
        }
      }, 1000);
    } catch (error) {
      console.error("[DownloadForm] フォーム送信失敗:", error);
      alert("フォームの送信に失敗しました。もう一度お試しください。");
    }
  }

  return (
    <DynamicMultiStepForm
      formProps={formProps}
      callback={callback}
    />
  );
}
